import { ref, computed, watch, onBeforeUnmount } from '@vue/composition-api'
import {
  DEFAULT_TOP_N,
  buildRankingUrl,
  normalizeTopN,
  pickTopFilterValues,
  primaryByColumn,
  resolveRankingTarget,
  serializeFilterValues,
  supportsTopN,
  supportsTopNDataset,
} from '../utils/topRanked'
import { getDatasetModule } from '../config/datasets'

const RANKING_DEBOUNCE_MS = 350

/**
 * "Top N by record count" for the chart's category axis.
 *
 * The Streamlit `query` parameter would express this in one SQL statement, but
 * it answers HTTP 500 for every value on the current backend (measured, even
 * for `SELECT 1 AS x`), so the ranking is resolved in two steps instead:
 * an aggregate read of `<column>,$count` carrying the chart's own filters and
 * time range, then the winning labels handed back to the chart through the
 * dataset's own filter parameter.
 *
 * The values are never persisted — they are derived from live data and would
 * go stale the moment the underlying records change.
 */
export function useTopRanked({
  selectedDataset,
  selectedView,
  selectedField,
  fieldOptions,
  saved = {},
}) {
  const initialCount = normalizeTopN(saved.topNCount)
  const enabled = ref(Boolean(saved.topNEnabled))
  const count = ref(initialCount ?? DEFAULT_TOP_N)
  const values = ref([])
  const loading = ref(false)
  const error = ref('')
  // The chart URL the current `values` were computed from, and the URL the
  // chart is asking about right now. Applying a ranking to a different URL
  // would restrict the chart to the previous filters' winners.
  const valuesSource = ref('')
  const currentSource = ref('')

  // While the input is transiently empty or invalid, keep using the last value
  // that was valid instead of falling back to the default. Tracked in the
  // setter rather than a watcher: Vue 2 flushes watchers on nextTick, so a
  // watcher would still hold the previous value within the same tick.
  const lastValidCount = ref(initialCount ?? DEFAULT_TOP_N)
  const effectiveCount = computed(() => normalizeTopN(count.value) ?? lastValidCount.value)

  // The ranked category is the first `by` column; a second field only colours
  // the bars, so ranking by it would cut the wrong axis.
  const rankedColumn = computed(() => {
    if (!selectedField.value) return ''
    const option = (fieldOptions.value || []).find(f => f.value === selectedField.value)
    return primaryByColumn(option?.rawKey || selectedField.value)
  })

  const rankingTarget = computed(() => resolveRankingTarget(
    selectedDataset.value,
    rankedColumn.value,
    getDatasetModule(selectedDataset.value)?.rankingTargets,
  ))

  /** The dataset gate alone — whether the control is worth showing at all. */
  const offered = computed(() => supportsTopNDataset(selectedDataset.value))

  const available = computed(() => (
    offered.value
    && supportsTopN(selectedView.value)
    && Boolean(rankedColumn.value)
    && Boolean(rankingTarget.value)
  ))

  /** Why the control is off, so the panel can say something useful. */
  const unavailableReason = computed(() => {
    if (available.value || !selectedView.value) return ''
    if (!offered.value) return 'No disponible en este modelo'
    if (!supportsTopN(selectedView.value)) return 'No disponible en esta gráfica'
    if (!rankedColumn.value) return 'Selecciona un campo'
    return 'El campo no admite ranking'
  })

  const active = computed(() => enabled.value && available.value)

  // Contract consumed by useChartUrl. The ranking is only applied to the very
  // URL it was computed from, so a filter or time-range change cannot render a
  // chart restricted to the previous rowset's winners.
  const topRanked = computed(() => ({
    enabled: active.value,
    param: rankingTarget.value?.param || '',
    column: rankedColumn.value,
    value: valuesSource.value && valuesSource.value === currentSource.value
      ? serializeFilterValues(values.value)
      : '',
  }))

  const commitCount = () => {
    count.value = effectiveCount.value
  }

  const setEnabled = (next) => {
    enabled.value = next
    if (next) commitCount()
    else { values.value = []; valuesSource.value = '' }
  }

  const updateCount = (value) => {
    count.value = value
    const normalized = normalizeTopN(value)
    if (normalized !== null) lastValidCount.value = normalized
  }

  // A ranking computed for another dataset/field/view must never survive the
  // change: it would restrict the new chart to unrelated values.
  watch([selectedDataset, selectedField, selectedView], () => {
    values.value = []
    valuesSource.value = ''
  })

  /**
   * Wire the aggregate read. Kept separate from the state above because the
   * URL comes from `useChartUrl`, which itself consumes `topRanked` — binding
   * afterwards is what breaks the cycle.
   */
  const bindRankingSource = (chartUrlWithoutTopRanked) => {
    let debounceTimer = null
    let latestRequestId = 0

    watch(chartUrlWithoutTopRanked, (url) => { currentSource.value = url || '' }, { immediate: true })

    const fetchRanking = async () => {
      if (!active.value) {
        values.value = []
        valuesSource.value = ''
        error.value = ''
        return
      }

      // Captured before awaiting: the ranking is only valid for this URL.
      const source = chartUrlWithoutTopRanked.value
      const target = rankingTarget.value
      const url = buildRankingUrl(source, target)
      if (!url) return

      const requestId = ++latestRequestId
      loading.value = true
      error.value = ''

      try {
        const response = await fetch(url)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const payload = await response.json()
        // A slower earlier request must not overwrite a newer ranking.
        if (requestId !== latestRequestId) return
        values.value = pickTopFilterValues(payload, target, effectiveCount.value)
        valuesSource.value = values.value.length ? source : ''
        if (!values.value.length) error.value = 'Sin datos para ordenar'
      } catch (e) {
        console.warn('[ChartsEmbedder] Top-N ranking request failed:', e)
        if (requestId !== latestRequestId) return
        // Leave the chart unrestricted rather than showing an arbitrary subset.
        values.value = []
        valuesSource.value = ''
        error.value = 'No se pudo calcular el ranking'
      } finally {
        if (requestId === latestRequestId) loading.value = false
      }
    }

    watch(
      () => [active.value, effectiveCount.value, chartUrlWithoutTopRanked.value].join('|'),
      () => {
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(fetchRanking, RANKING_DEBOUNCE_MS)
      },
      { immediate: true },
    )

    onBeforeUnmount(() => clearTimeout(debounceTimer))
  }

  return {
    topRankedEnabled: enabled,
    topRankedCount: count,
    topRankedEffectiveCount: effectiveCount,
    topRankedValues: values,
    topRankedLoading: loading,
    topRankedError: error,
    topRankedAvailable: available,
    topRankedOffered: offered,
    // Whether the chart is actually being restricted right now. Consumers use
    // it to disable exports that cannot honour the restriction.
    topRankedActive: active,
    topRankedUnavailableReason: unavailableReason,
    topRanked,
    setTopRankedEnabled: setEnabled,
    updateTopRankedCount: updateCount,
    commitTopRankedCount: commitCount,
    bindRankingSource,
  }
}
